import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChevronIcon from '../../assets/icons/chevron.svg?react';
import type { InterventionIcon, PatientTreatmentPlanDetailsResponse } from '../../api/generated';
import { InterventionIconComponent } from '../InterventionIcon/InterventionIcon';
import { ColorBar } from '../UI';
import type { TreatmentPlanStatus } from '../../utils/treatmentPlanStatus';
import { formatDateRangeSmart } from '../../utils/dateUtils';
import styles from './PatientTreatmentPlanCard.module.css';

interface PatientTreatmentPlanCardProps {
  plan: PatientTreatmentPlanDetailsResponse;
  status: TreatmentPlanStatus;
}

export const PatientTreatmentPlanCard = ({ plan, status }: PatientTreatmentPlanCardProps) => {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const statusLabel =
    status === 'active'
      ? t('patient-treatments-status-active')
      : status === 'upcoming'
        ? t('patient-treatments-status-upcoming')
        : null;

  const weekRangeText = t('patient-treatments-week-range', {
    start: plan.startWeek,
    end: plan.endWeek,
    year: plan.year
  });

  const sortedGroups = useMemo(
    () => [...plan.interventionGroups].sort((a, b) => a.startDate.localeCompare(b.startDate)),
    [plan.interventionGroups]
  );

  return (
    <div className={styles.card}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <ColorBar color={plan.strokeColor} />
        <div className={styles.body}>
          <span className={styles.name}>{plan.name}</span>
          <span className={styles.caption}>
            {statusLabel ? `${statusLabel} · ${weekRangeText}` : weekRangeText}
          </span>
        </div>
        <span className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}>
          <ChevronIcon />
        </span>
      </button>
      {isExpanded && (
        <ul className={styles.groupList}>
          {sortedGroups.map((group) => (
            <li
              key={group.id}
              className={`${styles.groupRow} ${group.assigned ? '' : styles.groupRowUnassigned}`}
            >
              <span className={styles.groupIcon}>
                <InterventionIconComponent icon={group.icon as InterventionIcon | undefined} />
              </span>
              <span className={styles.groupName}>{group.name}</span>
              <span className={styles.groupDates}>
                {formatDateRangeSmart(group.startDate, group.endDate, i18n.language)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
